import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { enableWebPush, disableWebPush } from '../lib/webPush'
import {
  Avatar, Box, Chip, Divider, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Stack, Tooltip, Typography,
} from '@mui/material'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
// HSE fork: Build / TaskAlt / Bolt / Inventory2 / Category / Storefront /
// Warehouse icons are gone with the modules they marked. LocationOn is
// reinstated for the top-level Sites entry.
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useThemeStore } from '../store/themeStore'
import { useChatSocket } from '../hooks/useChatSocket'
import { api } from '../lib/api'
import { APP_NAME } from '../lib/branding'
import { BrandLogo } from '../components/BrandLogo'
import { CommandPalette } from '../components/CommandPalette'
import { NotificationBell } from '../components/NotificationDrawer'
import { GlobalProgressBar } from '../components/GlobalProgressBar'
import { CustomerSwitcher } from '../components/CustomerSwitcher'

const EXPANDED = 280
const COLLAPSED = 76

interface NavItem {
  to: string
  label: string
  icon: React.ElementType
  badge?: number
}
interface NavGroup {
  title?: string
  items: NavItem[]
}

// Primary nav — ordered by the user's daily flow:
//   1. Dashboard  — check status (glanced at every session)
//   2. Work       — today's work and incoming requests (main activity)
//   3. Resources  — assets, places, parts you reach for during work
//   4. Setup      — templates, automations, admin — configured occasionally
//   5. Reports    — reviewed less often, usually by managers
//
// Groups are intentionally lean so the whole sidebar fits one viewport
// without needing to scroll on typical laptop heights.
// HSE fork: flat, un-titled nav. Nothing beyond Overview / Reports /
// Sites / People survives from CMMS, so the section headers ("Work",
// "Resources", "Setup", "Reports") stopped earning their pixels —
// four items don't need scaffolding. Everything now lives in one
// group so the sidebar reads as a compact list.
const TOP_NAV: NavGroup[] = [
  {
    items: [
      { to: '/overview', label: 'Overview', icon: SpaceDashboardOutlinedIcon },
      { to: '/reports',  label: 'Reports',  icon: AssignmentOutlinedIcon },
      { to: '/sites',    label: 'Sites',    icon: LocationOnOutlinedIcon },
      { to: '/people',   label: 'People',   icon: GroupsOutlinedIcon },
      { to: '/checklists', label: 'Checklists', icon: ChecklistOutlinedIcon },
    ],
  },
]

// Secondary nav — pinned at the bottom, above the theme pill.
// HSE fork: Help & support removed.
const BOTTOM_NAV: NavItem[] = [
  { to: '/chats',    label: 'Chats',    icon: ChatBubbleOutlineOutlinedIcon },
  { to: '/settings', label: 'Settings', icon: SettingsOutlinedIcon },
]

import { alpha } from '@mui/material/styles'

// Sidebar-specific color tokens
const SIDEBAR_BG     = { light: '#FFFFFF',            dark: '#141821' }
const SIDEBAR_BORDER = { light: 'rgba(0,0,0,0.06)',   dark: 'rgba(255,255,255,0.06)' }
const SEARCH_BG      = { light: '#F4F4F6',            dark: 'rgba(255,255,255,0.04)' }
const TOGGLE_BG      = { light: '#F4F4F6',            dark: 'rgba(255,255,255,0.04)' }
const TOGGLE_ACTIVE  = { light: '#FFFFFF',            dark: 'rgba(255,255,255,0.10)' }

function pickMode<T>(mode: 'light' | 'dark', m: { light: T; dark: T }) {
  return m[mode]
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])
  const user = useAuthStore((s) => s.user)
  // Live org name for the sidebar header. The JWT only carries the
  // immutable `org_slug`, so without this we'd render the slug after
  // a rename in General Settings. Sharing the same `['org']` query
  // key as the settings page means a save there invalidates this
  // automatically — no event bus needed.
  const orgQ = useQuery({
    queryKey: ['org'],
    queryFn: () => api.orgs.getCurrent(),
    staleTime: 60_000,
    enabled: !!user,
  })
  const orgName = orgQ.data?.name
    ?? (user?.org_slug ? capitalise(user.org_slug) : APP_NAME)
  const { unreadTotal } = useChatSocket()
  const refreshToken = useAuthStore((s) => s.refreshToken)
  const clear = useAuthStore((s) => s.clear)
  const mode = useThemeStore((s) => s.mode)
  const setMode = useThemeStore((s) => s.setMode)
  const location = useLocation()
  const navigate = useNavigate()

  const width = collapsed ? COLLAPSED : EXPANDED
  const themeMode: 'light' | 'dark' = mode === 'dark' ? 'dark' : 'light'

  const initials = useMemo(() => (
    (user?.name ?? '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
  ), [user?.name])

  async function handleLogout() {
    // Best-effort — the browser stops pushing this device the
    // moment the row disappears server-side.
    await disableWebPush()
    if (refreshToken) {
      try { await api.auth.logout({ refresh_token: refreshToken }) } catch { /* ignore */ }
    }
    clear()
    navigate('/login', { replace: true })
  }

  // ── Web Push subscribe on first mount after login ────────────
  // Fires exactly once per session — subsequent renders short-circuit
  // via `attemptedPushEnable`. `enableWebPush()` is itself idempotent
  // (it re-uses an existing subscription), so even if this fires
  // more than once nothing bad happens; the ref just spares us a
  // network round-trip.
  const attemptedPushEnable = useRef(false)
  useEffect(() => {
    if (!user) return
    if (attemptedPushEnable.current) return
    attemptedPushEnable.current = true
    enableWebPush().catch(() => { /* silent — user can retry from settings later */ })
  }, [user])

  return (
    <Box sx={{ display: 'flex', height: '100dvh', overflow: 'hidden', bgcolor: 'background.default' }}>
      {/* ─── Sidebar ─── */}
      <Box
        component="aside"
        sx={{
          width,
          flexShrink: 0,
          transition: 'width 240ms cubic-bezier(0.32, 0.72, 0, 1)',
          bgcolor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SIDEBAR_BG),
          borderRight: '1px solid',
          borderColor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SIDEBAR_BORDER),
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
        }}
      >
        {/* ── Brand ── */}
        <Box
          sx={{
            px: collapsed ? 0 : 2.5,
            pt: 3, pb: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            gap: 1,
          }}
        >
          <Stack
            component={RouterLink}
            to="/overview"
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              textDecoration: 'none', color: 'inherit',
              overflow: 'hidden', flex: 1, minWidth: 0,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            {/* Landscape brand mark. Theme-aware:
                  • Light mode → native colours.
                  • Dark mode  → tries `sila-logo-dark.webp`
                    (white letters + orange dot preserved); falls
                    back to the light logo filtered white if the dark
                    file isn't uploaded yet. */}
            <BrandLogo
              sx={{ height: 32, width: 'auto', maxWidth: 96 }}
            />
            {!collapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 600, lineHeight: 1.2, letterSpacing: '-0.015em', fontSize: 15 }}
                  noWrap
                >
                  {orgName}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.1, fontSize: 12 }}
                  noWrap
                >
                  {APP_NAME} workspace
                </Typography>
              </Box>
            )}
          </Stack>
          {!collapsed && (
            <IconButton
              size="small"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
              sx={{ color: 'text.secondary', width: 28, height: 28 }}
            >
              <ChevronLeftIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>

        {/* ── Customer switcher (hidden when the user only has access
            to a single customer — most workspaces) ── */}
        <CustomerSwitcher collapsed={collapsed} />

        {/* ── Search ── */}
        <Box sx={{ px: collapsed ? 0 : 2.5, pb: 2, display: 'flex', justifyContent: 'center' }}>
          {!collapsed ? (
            <Box
              onClick={() => setSearchOpen(true)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                height: 38,
                px: 1.75,
                borderRadius: 2,
                bgcolor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SEARCH_BG),
                border: '1px solid transparent',
                cursor: 'pointer',
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <SearchOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', mr: 1.25 }} />
              <Typography sx={{ flex: 1, fontSize: 13.5, color: 'text.secondary', letterSpacing: '-0.005em', userSelect: 'none' }}>
                Search...
              </Typography>
              <KbdHint>⌘K</KbdHint>
            </Box>
          ) : (
            <Tooltip title="Search (⌘K)" placement="right" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={{ color: 'text.secondary', width: 36, height: 36 }}>
                <SearchOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              sx={{ color: 'text.secondary', width: 30, height: 30 }}
            >
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}

        {/* ── Primary nav (scrollable, but scrollbar stays invisible until hover) ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pb: 1,
            minHeight: 0,
            // Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: 'transparent transparent',
            // WebKit — invisible by default, subtle on hover
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: 'transparent',
              borderRadius: 999,
              transition: 'background-color 160ms ease',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              background: (t) =>
                t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
            },
            '&:hover': {
              scrollbarColor: (t) =>
                t.palette.mode === 'light'
                  ? 'rgba(0,0,0,0.12) transparent'
                  : 'rgba(255,255,255,0.12) transparent',
            },
          }}
        >
          {TOP_NAV.map((group, gi) => (
            <Box key={group.title ?? `overview-${gi}`} sx={{ mb: gi === TOP_NAV.length - 1 ? 0 : 1.5 }}>
              {group.title && !collapsed && <SectionLabel>{group.title}</SectionLabel>}
              {group.title && collapsed && gi > 0 && (
                <Divider
                  sx={{
                    mx: 2, my: 1,
                    borderColor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SIDEBAR_BORDER),
                  }}
                />
              )}
              <List disablePadding sx={{ px: collapsed ? 0 : 1.5 }}>
                {group.items.map((item) => (
                  <NavRow
                    key={item.to}
                    item={item}
                    collapsed={collapsed}
                    active={isActive(location.pathname, item.to)}
                    mode={themeMode}
                  />
                ))}
              </List>
            </Box>
          ))}
        </Box>

        {/* ── Bottom sticky nav (Chats / Settings / Help) ── */}
        <Box>
          <Divider
            sx={{
              mx: collapsed ? 2 : 2.5,
              borderColor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SIDEBAR_BORDER),
            }}
          />
          <List disablePadding sx={{ px: collapsed ? 0 : 1.5, py: 1 }}>
            {BOTTOM_NAV.map((item) => (
              <NavRow
                key={item.to}
                item={item.to === '/chats' ? { ...item, badge: unreadTotal } : item}
                collapsed={collapsed}
                active={isActive(location.pathname, item.to)}
                mode={themeMode}
              />
            ))}
          </List>
        </Box>

        {/* ── Theme toggle pill ── */}
        <Box sx={{ px: collapsed ? 0 : 2.5, pb: 2, display: 'flex', justifyContent: 'center' }}>
          {!collapsed ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                p: 0.5,
                borderRadius: 2,
                bgcolor: (t) => pickMode(t.palette.mode as 'light' | 'dark', TOGGLE_BG),
              }}
            >
              <ThemeBtn
                active={mode === 'light'}
                onClick={() => setMode('light')}
                icon={<LightModeOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Light"
              />
              <ThemeBtn
                active={mode === 'dark'}
                onClick={() => setMode('dark')}
                icon={<DarkModeOutlinedIcon sx={{ fontSize: 15 }} />}
                label="Dark"
              />
            </Box>
          ) : (
            <Tooltip
              title={mode === 'light' ? 'Switch to dark' : 'Switch to light'}
              placement="right"
              arrow
            >
              <IconButton
                size="small"
                onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
                sx={{ color: 'text.secondary', width: 36, height: 36 }}
              >
                {mode === 'light'
                  ? <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
                  : <LightModeOutlinedIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* ── User footer ── */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: (t) => pickMode(t.palette.mode as 'light' | 'dark', SIDEBAR_BORDER),
            p: collapsed ? 1.25 : 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 1.5,
          }}
        >
          <Avatar
            src={user?.avatar_url ?? undefined}
            sx={{
              width: 36, height: 36,
              bgcolor: 'primary.main',
              fontSize: 12.5, fontWeight: 600,
              letterSpacing: '-0.01em',
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
          {!collapsed && (
            <>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.25, letterSpacing: '-0.005em' }}
                  noWrap
                >
                  {user?.name ?? 'User'}
                </Typography>
                <Typography
                  sx={{ color: 'text.secondary', lineHeight: 1.2, fontSize: 11.5 }}
                  noWrap
                >
                  {user?.email}
                </Typography>
              </Box>
              <NotificationBell />
              <Tooltip title="Logout" arrow>
                <IconButton
                  size="small"
                  onClick={handleLogout}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* ─── Main ─── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100dvh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <GlobalProgressBar />
        <Outlet />
      </Box>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Box>
  )
}

// ─── Helpers & sub-components ────────────────────────────────────────────────

function isActive(pathname: string, to: string) {
  // Exact match for '/', else startsWith but avoid '/foo' matching '/foo-bar'
  if (to === pathname) return true
  return pathname.startsWith(to + '/')
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      sx={{
        display: 'block',
        px: 3, pt: 1.25, pb: 0.75,
        color: 'text.secondary',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontSize: 10.5,
      }}
    >
      {children}
    </Typography>
  )
}

function NavRow({
  item, collapsed, active, mode,
}: {
  item: NavItem
  collapsed: boolean
  active: boolean
  mode: 'light' | 'dark'
}) {
  const Icon = item.icon
  const button = (
    <ListItemButton
      component={NavLink}
      to={item.to}
      sx={{
        borderRadius: 2,
        px: collapsed ? 0 : 2,
        py: 1,
        mb: 0.25,
        minHeight: 38,
        justifyContent: collapsed ? 'center' : 'flex-start',
        color: active ? 'text.primary' : 'text.secondary',
        bgcolor: active
          ? (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.08 : 0.18)
          : 'transparent',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': {
          bgcolor: active
            ? (t) => alpha(t.palette.primary.main, t.palette.mode === 'light' ? 0.12 : 0.22)
            : pickMode(mode, { light: 'rgba(0,0,0,0.04)', dark: 'rgba(255,255,255,0.04)' }),
          color: 'text.primary',
        },
        ...(collapsed && { width: 44, height: 44, mx: 'auto' }),
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 1.75,
          color: active ? 'primary.main' : 'inherit',
          justifyContent: 'center',
          transition: 'color 120ms ease',
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </ListItemIcon>
      {!collapsed && (
        <>
          <ListItemText
            primary={item.label}
            slotProps={{
              primary: {
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                letterSpacing: '-0.005em',
              },
            }}
          />
          {typeof item.badge === 'number' && item.badge > 0 && (
            <Chip
              label={item.badge > 99 ? '99+' : item.badge}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 99,
                '& .MuiChip-label': { px: 0.85 },
                bgcolor: 'error.main',
                color: 'error.contrastText',
              }}
            />
          )}
        </>
      )}
    </ListItemButton>
  )
  return collapsed ? (
    <Tooltip title={item.label} placement="right" arrow>
      <span style={{ display: 'block' }}>{button}</span>
    </Tooltip>
  ) : button
}

function ThemeBtn({
  active, onClick, icon, label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.85,
        px: 1.5, py: 1,
        height: 34,
        border: 'none',
        borderRadius: 1.5,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        color: active ? 'text.primary' : 'text.secondary',
        bgcolor: active
          ? (t) => pickMode(t.palette.mode as 'light' | 'dark', TOGGLE_ACTIVE)
          : 'transparent',
        boxShadow: active
          ? (t) => t.palette.mode === 'light'
              ? '0 1px 2px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)'
              : '0 1px 2px rgba(0,0,0,0.4)'
          : 'none',
        transition: 'background-color 140ms ease, color 140ms ease, box-shadow 140ms ease',
      }}
    >
      {icon}
      <span>{label}</span>
    </Box>
  )
}

function KbdHint({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        px: 0.85, py: 0.15,
        borderRadius: 1,
        fontSize: 10.5,
        fontFamily: 'ui-monospace, monospace',
        fontWeight: 600,
        color: 'text.secondary',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        lineHeight: '15px',
        minWidth: 24,
        textAlign: 'center',
      }}
    >
      {children}
    </Box>
  )
}

function capitalise(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
